import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import eventoService from '../services/eventoService';
import fotoService from '../services/fotoService';
import LoadingSpinner from '../components/LoadingSpinner';
import './EventosList.css';

// Debe coincidir con COMISION_PLATAFORMA del backend (StripeCheckoutService)
const COMISION_PLATAFORMA = 0.20;
const PRECIO_MINIMO_STRIPE = 10;
const PRECIOS_RAPIDOS = [50, 80, 100, 150];
const SUBTIPOS_FIESTA = ['Boda', 'Graduación', 'Baby Shower', 'Despedida', 'XV Años', 'Otro'];
const PASOS = ['Tipo', 'Detalles', 'Precio'];

const TIPOS_EVENTO = [
  { valor: 'CARRERA', icono: '🏃', titulo: 'Carrera', texto: 'Los atletas encuentran sus fotos con su número de corredor.' },
  { valor: 'AGUAS_ABIERTAS', icono: '🏊', titulo: 'Aguas abiertas', texto: 'Natación o triatlón, con el dorsal escrito en la piel.' },
  { valor: 'FIESTA', icono: '🎉', titulo: 'Fiesta', texto: 'Bodas, graduaciones y más: se buscan con una selfie.' }
];

const FORM_INICIAL = {
  nombre: '',
  descripcion: '',
  fecha: '',
  ubicacion: '',
  precioFoto: '100',
  descuentosActivos: false,
  descuentos: [],
  tipoEvento: 'CARRERA',
  subtipo: '',
  deteccionPielActiva: false
};

const formatMoney = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const infoTipo = (evento) => {
  if (evento.tipoEvento === 'FIESTA') return { clase: 'fiesta', texto: `🎉 ${evento.subtipo || 'Fiesta'}` };
  if (evento.deteccionPielActiva) return { clase: 'aguas', texto: '🏊 Aguas abiertas' };
  return { clase: 'carrera', texto: '🏃 Carrera' };
};

const porcentajeParaCantidad = (descuentos, cantidad) =>
  descuentos.reduce((max, d) => {
    const minimo = parseInt(d.cantidadFotos, 10);
    const pct = parseInt(d.porcentajeDescuento, 10);
    return minimo >= 2 && pct > 0 && cantidad >= minimo ? Math.max(max, pct) : max;
  }, 0);

const EventosList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cerrandoEventoId, setCerrandoEventoId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [paso, setPaso] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState('');
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkEvento, setLinkEvento] = useState(null);
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [nuevoPrecio, setNuevoPrecio] = useState('');
  const [formData, setFormData] = useState(FORM_INICIAL);

  const abrirCrear = () => {
    setFormData(FORM_INICIAL);
    setPaso(0);
    setShowModal(true);
  };

  const seleccionarTipo = (valor) => {
    if (valor === 'AGUAS_ABIERTAS') {
      setFormData(f => ({ ...f, tipoEvento: 'CARRERA', deteccionPielActiva: true, subtipo: '' }));
    } else {
      setFormData(f => ({ ...f, tipoEvento: valor, deteccionPielActiva: false, subtipo: '' }));
    }
  };

  const puedeAvanzarPaso = () =>
    paso !== 1 || (formData.nombre.trim() !== '' && formData.fecha !== '');

  const addDescuento = (cantidad = '', porcentaje = '') => setFormData(f => ({
    ...f,
    descuentos: [...f.descuentos, { cantidadFotos: String(cantidad), porcentajeDescuento: String(porcentaje) }]
  }));

  const sugerirDescuentos = () => setFormData(f => ({
    ...f,
    descuentos: [
      { cantidadFotos: '2', porcentajeDescuento: '10' },
      { cantidadFotos: '3', porcentajeDescuento: '20' }
    ]
  }));

  const removeDescuento = (idx) => {
    setFormData(f => ({ ...f, descuentos: f.descuentos.filter((_, i) => i !== idx) }));
  };

  const updateDescuento = (idx, field, value) => {
    setFormData(f => ({
      ...f,
      descuentos: f.descuentos.map((d, i) => i === idx ? { ...d, [field]: value } : d)
    }));
  };

  useEffect(() => {
    loadEventos();
  }, []);

  // El Dashboard enlaza aquí con state.crear para abrir directo el asistente
  useEffect(() => {
    if (location.state?.crear) {
      setFormData(FORM_INICIAL);
      setPaso(0);
      setShowModal(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const loadEventos = async () => {
    try {
      console.log('Intentando cargar eventos...');
      const data = await eventoService.getAll();
      console.log('Eventos recibidos:', data);
      setEventos(data);
    } catch (err) {
      console.error('Error al cargar eventos:', err);
      console.error('Respuesta del error:', err.response);
      alert('Error al cargar eventos: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (paso < PASOS.length - 1) {
      if (puedeAvanzarPaso()) setPaso(paso + 1);
      return;
    }

    const precio = parseFloat(formData.precioFoto);
    if (isNaN(precio) || precio <= 0) {
      alert('Por favor ingresa un precio válido mayor a 0');
      return;
    }

    const descuentosValidos = formData.descuentos
      .map(d => ({
        cantidadFotos: parseInt(d.cantidadFotos, 10),
        porcentajeDescuento: parseInt(d.porcentajeDescuento, 10)
      }))
      .filter(d => d.cantidadFotos >= 2 && d.porcentajeDescuento >= 1 && d.porcentajeDescuento <= 100);

    try {
      setGuardando(true);
      await eventoService.create({
        ...formData,
        precioFoto: precio,
        descuentos: descuentosValidos,
        descuentosActivos: descuentosValidos.length > 0
      });
      setToast('🎉 ¡Evento creado! Ya puedes subir tus fotos.');
      setShowModal(false);
      setFormData(FORM_INICIAL);
      loadEventos();
    } catch (err) {
      console.error('Error completo:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Error desconocido';
      alert('Error al crear evento: ' + errorMsg);
    } finally {
      setGuardando(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este evento?')) {
      try {
        await eventoService.delete(id);
        loadEventos();
      } catch (err) {
        alert('Error al eliminar evento');
      }
    }
  };

  const handleCerrarEvento = async (evento) => {
    if (!window.confirm(
      `¿Cerrar el evento "${evento.nombre}"?\n\n` +
      'Se enviará el mensaje de WhatsApp aprobado a los atletas que se registraron ' +
      'y no encontramos su foto, avisándoles que el evento ya cerró.'
    )) {
      return;
    }
    try {
      setCerrandoEventoId(evento.id);
      const resultado = await fotoService.cerrarEvento(evento.id);
      alert(`✅ ${resultado.mensaje}\n\nAtletas notificados: ${resultado.notificados}`);
      loadEventos();
    } catch (err) {
      alert('Error al cerrar el evento: ' + (err.response?.data?.error || err.message));
    } finally {
      setCerrandoEventoId(null);
    }
  };

  const handleOpenPriceModal = (evento) => {
    setSelectedEvento(evento);
    setNuevoPrecio(evento.precioFoto);
    setShowPriceModal(true);
  };

  const handleClosePriceModal = () => {
    setShowPriceModal(false);
    setSelectedEvento(null);
    setNuevoPrecio('');
  };

  const handleUpdatePrecio = async (e) => {
    e.preventDefault();
    const precio = parseFloat(nuevoPrecio);
    
    if (isNaN(precio) || precio <= 0) {
      alert('Por favor ingresa un precio válido mayor a 0');
      return;
    }

    try {
      const response = await eventoService.updatePrecio(selectedEvento.id, precio);
      alert(`✅ ${response.mensaje}\n\nEvento actualizado: $${response.precio} MXN\nFotos actualizadas: ${response.fotosActualizadas}`);
      handleClosePriceModal();
      loadEventos();
    } catch (err) {
      console.error('Error al actualizar precio:', err);
      alert('Error al actualizar precio: ' + (err.response?.data || err.message));
    }
  };

  if (loading) return <LoadingSpinner mensaje="Cargando eventos..." />;

  const tipoSeleccionado = formData.tipoEvento === 'CARRERA' && formData.deteccionPielActiva
    ? 'AGUAS_ABIERTAS'
    : formData.tipoEvento;
  const precioNum = parseFloat(formData.precioFoto) || 0;
  const totalFotos = eventos.reduce((s, e) => s + (e.totalFotosSubidas || 0), 0);

  return (
    <div className="ev-page">
      <div className="ev-header">
        <div>
          <h1>Mis eventos</h1>
          <p>Crea eventos, sube tus fotos y comparte el link para vender por WhatsApp.</p>
          <div className="ev-summary">
            <span><strong>{eventos.length}</strong> {eventos.length === 1 ? 'evento' : 'eventos'}</span>
            <span><strong>{totalFotos.toLocaleString('es-MX')}</strong> fotos subidas</span>
          </div>
        </div>
        <button className="ev-btn ev-btn-primary ev-btn-lg" onClick={abrirCrear}>＋ Crear evento</button>
      </div>

      {eventos.length === 0 && (
        <div className="ev-empty">
          <span>🎟️</span>
          <h3>Aún no tienes eventos</h3>
          <p>Crea tu primer evento para empezar a subir fotos y vender.</p>
          <button className="ev-btn ev-btn-primary" onClick={abrirCrear}>Crear mi primer evento</button>
        </div>
      )}

      <div className="ev-grid">
        {eventos.map((evento, idx) => {
          const tipo = infoTipo(evento);
          const tieneDescuentos = evento.descuentosActivos && evento.descuentos?.length > 0;
          return (
            <div key={evento.id} className="ev-card" style={{ animationDelay: `${Math.min(idx, 8) * 60}ms` }}>
              <div className={`ev-card-top ev-type-${tipo.clase}`}>
                <span className="ev-badge">{tipo.texto}</span>
                <span className="ev-date">📅 {evento.fecha}</span>
              </div>

              <div className="ev-card-body">
                <h3>{evento.nombre}</h3>
                {evento.descripcion && <p className="ev-desc">{evento.descripcion}</p>}
                {evento.ubicacion && <p className="ev-location">📍 {evento.ubicacion}</p>}

                <div className="ev-stats">
                  <div><strong>{evento.totalFotosSubidas || 0}</strong><span>Fotos</span></div>
                  <div><strong>${evento.precioFoto}</strong><span>MXN por foto</span></div>
                </div>

                <div className="ev-chips">
                  {tieneDescuentos
                    ? evento.descuentos.map((d, i) => (
                      <span key={i} className="ev-chip ev-chip-deal">{d.cantidadFotos}+ fotos · -{d.porcentajeDescuento}%</span>
                    ))
                    : <span className="ev-chip ev-chip-muted">Sin descuentos</span>}
                </div>
              </div>

              <div className="ev-card-actions">
                <Link to={`/upload/${evento.id}`} className="ev-btn ev-btn-primary">⬆️ Subir fotos</Link>
                <Link to={`/eventos/${evento.id}/fotos`} className="ev-btn ev-btn-soft">
                  🖼️ Ver fotos ({evento.totalFotosSubidas || 0})
                </Link>
                <div className="ev-tools">
                  <button
                    className="ev-tool"
                    onClick={() => handleOpenPriceModal(evento)}
                    title="Editar precio y actualizar todas las fotos"
                  >
                    💲 Precio
                  </button>
                  <button
                    className="ev-tool"
                    onClick={() => { setLinkEvento(evento); setShowLinkModal(true); }}
                    title="Generar link y QR para este evento"
                  >
                    🔗 Link + QR
                  </button>
                  <button
                    className="ev-tool"
                    onClick={() => handleCerrarEvento(evento)}
                    disabled={cerrandoEventoId === evento.id}
                    title="Avisar por WhatsApp a los atletas registrados que no encontramos su foto"
                  >
                    {cerrandoEventoId === evento.id ? 'Cerrando...' : '🔒 Cerrar'}
                  </button>
                  <button
                    className="ev-tool ev-tool-danger"
                    onClick={() => handleDelete(evento.id)}
                    title="Eliminar evento"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {toast && <div className="ev-toast" role="status">{toast}</div>}

      {showModal && (
        <div
          className="ev-backdrop"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !guardando) setShowModal(false); }}
        >
          <div className="ev-modal ev-wizard" role="dialog" aria-modal="true" aria-label="Crear evento">
            <div className="ev-wizard-head">
              <h2>Crear nuevo evento</h2>
              <button type="button" className="ev-close" onClick={() => setShowModal(false)} aria-label="Cerrar">×</button>
            </div>

            <ol className="ev-stepper">
              {PASOS.map((nombrePaso, i) => (
                <li key={nombrePaso} className={`ev-stepper-item${i === paso ? ' active' : ''}${i < paso ? ' done' : ''}`}>
                  <span>{i < paso ? '✓' : i + 1}</span>{nombrePaso}
                </li>
              ))}
            </ol>
            <div className="ev-progress"><div style={{ width: `${((paso + 1) / PASOS.length) * 100}%` }} /></div>

            <form onSubmit={handleSubmit}>
              <div className="ev-step" key={paso}>
                {paso === 0 && (
                  <>
                    <h3 className="ev-step-title">¿Qué tipo de evento es?</h3>
                    <div className="ev-type-grid">
                      {TIPOS_EVENTO.map(t => (
                        <button
                          type="button"
                          key={t.valor}
                          className={`ev-type-card${tipoSeleccionado === t.valor ? ' selected' : ''}`}
                          onClick={() => seleccionarTipo(t.valor)}
                        >
                          <span className="ev-type-icon">{t.icono}</span>
                          <strong>{t.titulo}</strong>
                          <span>{t.texto}</span>
                        </button>
                      ))}
                    </div>
                    {formData.tipoEvento === 'FIESTA' && (
                      <div className="ev-subtipos">
                        <span>¿Qué tipo de fiesta?</span>
                        <div>
                          {SUBTIPOS_FIESTA.map(s => (
                            <button
                              type="button"
                              key={s}
                              className={`ev-chip ev-chip-btn${formData.subtipo === s ? ' selected' : ''}`}
                              onClick={() => setFormData(f => ({ ...f, subtipo: s }))}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {paso === 1 && (
                  <>
                    <h3 className="ev-step-title">Cuéntanos del evento</h3>
                    <div className="form-group">
                      <label>Nombre del evento *</label>
                      <input
                        type="text"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Ej. Medio Maratón de Guadalajara"
                        autoFocus
                      />
                    </div>
                    <div className="ev-row">
                      <div className="form-group">
                        <label>Fecha *</label>
                        <input
                          type="date"
                          value={formData.fecha}
                          onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Ubicación</label>
                        <input
                          type="text"
                          value={formData.ubicacion}
                          onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                          placeholder="Ciudad o sede"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Descripción</label>
                      <textarea
                        value={formData.descripcion}
                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                        rows={3}
                        placeholder="Opcional: cuéntales a los atletas de qué trata"
                      />
                    </div>
                  </>
                )}

                {paso === 2 && (
                  <>
                    <h3 className="ev-step-title">Define tu precio</h3>
                    <div className="form-group">
                      <label>Precio por foto (MXN) *</label>
                      <div className="ev-price-input">
                        <span>$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.precioFoto}
                          onChange={(e) => setFormData({ ...formData, precioFoto: e.target.value })}
                        />
                      </div>
                      <div className="ev-quick-prices">
                        {PRECIOS_RAPIDOS.map(p => (
                          <button
                            type="button"
                            key={p}
                            className={`ev-chip ev-chip-btn${precioNum === p ? ' selected' : ''}`}
                            onClick={() => setFormData(f => ({ ...f, precioFoto: String(p) }))}
                          >
                            ${p}
                          </button>
                        ))}
                      </div>
                      {precioNum > 0 && precioNum < PRECIO_MINIMO_STRIPE && (
                        <p className="ev-warn">⚠️ El mínimo para cobrar con tarjeta es ${PRECIO_MINIMO_STRIPE} MXN por compra.</p>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Descuentos por cantidad <small>(opcional)</small></label>
                      <div className="descuentos-dinamicos">
                        <p className="descuentos-hint">Los compradores compran más cuando hay descuentos por cantidad</p>
                        {formData.descuentos.map((d, idx) => (
                          <div key={idx} className="descuento-row">
                            <div className="descuento-field">
                              <label>Cantidad de fotos</label>
                              <input
                                type="number"
                                min="2"
                                value={d.cantidadFotos}
                                onChange={(e) => updateDescuento(idx, 'cantidadFotos', e.target.value)}
                                placeholder="Ej: 2"
                              />
                            </div>
                            <div className="descuento-field">
                              <label>Descuento (%)</label>
                              <input
                                type="number"
                                min="1"
                                max="100"
                                value={d.porcentajeDescuento}
                                onChange={(e) => updateDescuento(idx, 'porcentajeDescuento', e.target.value)}
                                placeholder="Ej: 20"
                              />
                            </div>
                            <button type="button" className="btn-eliminar-descuento" onClick={() => removeDescuento(idx)}>Quitar</button>
                          </div>
                        ))}
                        <div className="descuentos-botones">
                          <button type="button" className="btn-agregar-rango" onClick={() => addDescuento()}>+ Agregar rango</button>
                          {formData.descuentos.length === 0 && (
                            <button type="button" className="btn-sugerir" onClick={sugerirDescuentos}>✨ Sugerir: 2 fotos -10%, 3 fotos -20%</button>
                          )}
                        </div>
                      </div>
                    </div>

                    {precioNum > 0 && (
                      <div className="ev-sim">
                        <h4>Así se verían tus ventas</h4>
                        {[1, 3, 5].map(n => {
                          const pct = porcentajeParaCantidad(formData.descuentos, n);
                          const total = precioNum * n * (1 - pct / 100);
                          return (
                            <div key={n} className="ev-sim-row">
                              <span>{n} {n === 1 ? 'foto' : 'fotos'}{pct > 0 && <em>-{pct}%</em>}</span>
                              <span>Paga <strong>{formatMoney(total)}</strong></span>
                              <span className="ev-sim-gain">Tú recibes ≈ <strong>{formatMoney(total * (1 - COMISION_PLATAFORMA))}</strong></span>
                            </div>
                          );
                        })}
                        <p className="ev-sim-note">Estimado con el 80% para ti, una vez que conectes tu cuenta de Stripe en Mi Cuenta.</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="ev-wizard-actions">
                {paso > 0 ? (
                  <button type="button" className="ev-btn ev-btn-ghost" onClick={() => setPaso(paso - 1)}>← Atrás</button>
                ) : (
                  <button type="button" className="ev-btn ev-btn-ghost" onClick={() => setShowModal(false)}>Cancelar</button>
                )}
                {paso < PASOS.length - 1 ? (
                  <button
                    key="siguiente"
                    type="button"
                    className="ev-btn ev-btn-primary"
                    disabled={!puedeAvanzarPaso()}
                    onClick={(e) => { e.preventDefault(); setPaso(paso + 1); }}
                  >
                    Siguiente →
                  </button>
                ) : (
                  <button key="crear" type="submit" className="ev-btn ev-btn-primary" disabled={guardando}>
                    {guardando ? 'Creando...' : '✓ Crear evento'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {showPriceModal && selectedEvento && (
        <div className="ev-backdrop">
          <div className="ev-modal">
            <h2>Editar Precio del Evento</h2>
            <div className="price-modal-info">
              <p><strong>Evento:</strong> {selectedEvento.nombre}</p>
              <p><strong>Precio actual:</strong> ${selectedEvento.precioFoto} MXN</p>
              <p><strong>Fotos en el evento:</strong> {selectedEvento.totalFotosSubidas || 0}</p>
              <div className="alert-info">
                ℹ️ <strong>Importante:</strong> Este cambio actualizará automáticamente el precio de todas las fotos del evento que no estén vendidas.
              </div>
            </div>
            <form onSubmit={handleUpdatePrecio}>
              <div className="form-group">
                <label>Nuevo Precio por Foto (MXN) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={nuevoPrecio}
                  onChange={(e) => setNuevoPrecio(e.target.value)}
                  placeholder="Ej: 35.00"
                  autoFocus
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="submit" className="button">Actualizar Precio</button>
                <button 
                  type="button" 
                  className="button button-secondary" 
                  onClick={handleClosePriceModal}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showLinkModal && linkEvento && (() => {
        const nombre = linkEvento.nombre || `Evento ${linkEvento.id}`;
        // Mismo texto para ambos tipos: no se precompleta el número de corredor
        // para que el bot pueda ofrecer al atleta elegir entre número o selfie.
        const textoBase = `Hola, quiero buscar mis fotos del evento "${nombre}" EVENTO ${linkEvento.id}`;
        const textoWa = encodeURIComponent(textoBase);
        // El "1" después del 52 es requerido por versiones viejas de WhatsApp en
        // algunos teléfonos para resolver números móviles de México (regla legacy
        // de numeración MX) - sin él, esos equipos no logran abrir el chat.
        const waLink = `https://wa.me/5213328401723?text=${textoWa}`;
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(waLink)}`;
        return (
          <div className="ev-backdrop">
            <div className="ev-modal link-modal-content">
              <h2>🔗 Link del Evento</h2>
              <p className="link-modal-nombre">{linkEvento.nombre}</p>
              <p className="link-modal-hint">Comparte este link o QR con los corredores. Al abrirlo se conectan directo a este evento en WhatsApp.</p>

              <div className="link-modal-qr">
                <img src={qrUrl} alt="QR Code" />
              </div>

              <div className="link-modal-url">
                <input type="text" readOnly value={waLink} />
                <button
                  className="button"
                  onClick={() => { navigator.clipboard.writeText(waLink); setToast('🔗 ¡Link copiado!'); }}
                >
                  Copiar
                </button>
              </div>

              <div className="modal-actions" style={{marginTop: '16px'}}>
                <a href={waLink} target="_blank" rel="noreferrer" className="button">
                  Abrir en WhatsApp
                </a>
                <button className="button button-secondary" onClick={() => setShowLinkModal(false)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default EventosList;
